import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CastLibrary from "./CastLibrary";
import NewStory from "./NewStory";
import Episodes from "./Episodes";
import { Clapperboard, Users, Wand2, Film, LogOut } from "lucide-react";

const NAV = [
  { id: "cast", label: "Cast Library", icon: Users, testid: "nav-cast-library" },
  { id: "new", label: "New Story", icon: Wand2, testid: "nav-new-story" },
  { id: "episodes", label: "Episodes", icon: Film, testid: "nav-episodes" },
];

export default function StudioShell() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("new");
  const [cast, setCast] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [music, setMusic] = useState([]);
  const [models, setModels] = useState([]);

  const refreshCast = () => api.get("/cast").then((r) => setCast(r.data)).catch(() => {});
  const refreshEpisodes = () => api.get("/episodes").then((r) => setEpisodes(r.data)).catch(() => {});

  useEffect(() => {
    refreshCast();
    refreshEpisodes();
    api.get("/music").then((r) => setMusic(r.data)).catch(() => {});
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
  }, []);

  return (
    <div className="app-bg">
      {/* top nav */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl candy-strawberry grid place-items-center"><Clapperboard className="h-5 w-5 text-white" /></div>
            <div className="leading-none">
              <span className="font-display text-lg font-bold">FruitLove</span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-slate-400 block">Studio</span>
            </div>
          </div>

          <nav className="hidden sm:flex items-center gap-1 bg-black/25 rounded-full p-1">
            {NAV.map((n) => {
              const on = view === n.id;
              return (
                <button key={n.id} onClick={() => setView(n.id)} data-testid={n.testid}
                  className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${on ? "text-white" : "text-slate-400 hover:text-white"}`}>
                  {on && <motion.span layoutId="navpill" className="absolute inset-0 candy-strawberry rounded-full" />}
                  <n.icon className="h-4 w-4 relative z-10" />
                  <span className="relative z-10">{n.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              <div className="h-8 w-8 rounded-full candy-blueberry grid place-items-center text-xs font-bold text-white">
                {(user?.name || "S")[0].toUpperCase()}
              </div>
            </div>
            <button onClick={logout} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-white/5 text-slate-400 hover:text-[#FF7675]" data-testid="logout-btn" title="Log out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* mobile nav */}
        <nav className="sm:hidden flex items-center gap-1 px-4 pb-3">
          {NAV.map((n) => {
            const on = view === n.id;
            return (
              <button key={n.id} onClick={() => setView(n.id)} data-testid={`${n.testid}-m`}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium ${on ? "candy-strawberry text-white" : "bg-black/25 text-slate-400"}`}>
                <n.icon className="h-3.5 w-3.5" /> {n.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {view === "cast" && <CastLibrary cast={cast} refresh={refreshCast} />}
        {view === "new" && (
          <NewStory
            cast={cast} music={music} models={models}
            refreshStories={refreshEpisodes}
            goEpisodes={() => { refreshEpisodes(); setView("episodes"); }}
          />
        )}
        {view === "episodes" && <Episodes episodes={episodes} goNewStory={() => setView("new")} />}
      </main>
    </div>
  );
}
